/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState, useLayoutEffect, useRef, useMemo, useCallback, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Fab, useBreakpoint } from '@one-thd/sui-atomic-components';
import { useDrag } from '@use-gesture/react';
import { ArrowBack, ArrowForward } from '@one-thd/sui-icons';
import debounce from './private/utils/debounce';

const PEEKVALUES = {
  sm: 0.2,
  md: 0.4,
  lg: 0.6
};

const DEFAULT_BREAKPOINTS = {
  sm: {
    slidesPerView: 3,
    slidesPerGroup: 3
  },
  md: {
    slidesPerView: 4,
    slidesPerGroup: 4
  },
  lg: {
    slidesPerView: 5,
    slidesPerGroup: 5
  },
  xl: {
    slidesPerView: 6,
    slidesPerGroup: 6
  },
  '2xl': {
    slidesPerView: 7,
    slidesPerGroup: 7
  }
};

const getSlideWidth = (containerWidth, slidesPerView) => {
  return (containerWidth / slidesPerView);
};

const Filmstrip = (props) => {
  const {
    action,
    activeIndex: activeSlideProp,
    alwaysShowControls = false,
    breakpoints = DEFAULT_BREAKPOINTS,
    children = [],
    disableMargin = false,
    hideControls = false,
    onChange,
    peek = 'sm',
    slidesPerView = 2,
    slidesPerGroup = 1,
    spaceBetween = 16,
    title
  } = props;

  const [currentSlide, setCurrentSlide] = useState(0);
  const containerRef = useRef(null);
  const skipListingRef = useRef(null);
  const startScrollLeft = useRef(0);
  const peekAmount = PEEKVALUES[peek] || 0.2;
  const matches = useBreakpoint('sm', { current: true });

  const breakpointValues = useMemo(() => {
    const currentBreakpoint = matches.current;
    if (breakpoints[currentBreakpoint]) {
      return {
        slidesPerView: breakpoints[currentBreakpoint].slidesPerView || slidesPerView,
        slidesPerGroup: breakpoints[currentBreakpoint].slidesPerGroup || slidesPerGroup,
        spaceBetween: breakpoints[currentBreakpoint].spaceBetween || spaceBetween
      };
    }
    return { slidesPerView, slidesPerGroup, spaceBetween };
  }, [matches.current, slidesPerView, slidesPerGroup, spaceBetween, breakpoints]);

  const calculateCurrentSlide = useCallback(() => {
    if (!containerRef.current) return 0;

    const { offsetWidth, scrollLeft } = containerRef.current;
    const slideWidth = getSlideWidth(offsetWidth, breakpointValues.slidesPerView + peekAmount);
    const slideIndex = Math.round((scrollLeft - breakpointValues.spaceBetween) / slideWidth);

    return Math.max(0, slideIndex);
  }, [breakpointValues.slidesPerView, peekAmount, children.length]);

  const changeActiveSlide = (event, newSlide) => {
    if (currentSlide !== newSlide) {
      setCurrentSlide(newSlide);
    }
    if (onChange) {
      onChange(event, newSlide);
    }
  };

  const handleScroll = debounce((event) => {
    const newSlide = calculateCurrentSlide();
    changeActiveSlide(event, newSlide);
  }, 60);

  // Prevent dragging of images and links
  useEffect(() => {
    const preventDragStart = (event) => {
      if (event.target.tagName === 'IMG' || event.target.closest('img') || event.target.tagName === 'A' || event.target.closest('a')) {
        event.preventDefault();
        return false;
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('dragstart', preventDragStart);
    }
    return () => {
      container.removeEventListener('dragstart', preventDragStart);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        handleScroll.clear();
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const updateSlideSize = (targetContainer) => {
    if (targetContainer.current && typeof window !== 'undefined') {
      const { offsetWidth } = targetContainer.current;
      const { innerWidth } = window;
      const slideWidth = getSlideWidth(Math.min(offsetWidth, innerWidth), breakpointValues.slidesPerView + peekAmount);
      const slides = targetContainer.current.querySelectorAll('.sui-carousel-slide');
      slides.forEach((slide) => {
        slide.style.width = `${slideWidth - breakpointValues.spaceBetween}px`;
      });
    }
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      updateSlideSize(containerRef);
      if (typeof ResizeObserver === 'undefined') {
        console.warn('ResizeObserver is not supported in this environment. Carousel slide width wont be updated.');
        return;
      }

      const resizeObserver = new ResizeObserver((entries) => {
        updateSlideSize(containerRef);
      });
      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [slidesPerView, spaceBetween, breakpoints, matches.current, peek]);

  useEffect(() => {
    let newSlide = Number(activeSlideProp);
    if (typeof newSlide === 'number' && newSlide >= 0 && newSlide < children.length && newSlide !== currentSlide) {
      if (currentSlide !== newSlide) {
        setCurrentSlide(newSlide);
        if (containerRef.current) {
          const { offsetWidth } = containerRef.current;
          const slideWidth = getSlideWidth(offsetWidth, breakpointValues.slidesPerView + peekAmount);
          const newScrollLeft = slideWidth * newSlide;
          containerRef.current.scrollTo({
            left: newScrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [activeSlideProp, children.length, breakpointValues.slidesPerView, peekAmount]);

  const nextSlide = (event, slides) => {
    if (containerRef.current) {
      const { offsetWidth, scrollLeft } = containerRef.current;
      const slideWidth = getSlideWidth(offsetWidth, breakpointValues.slidesPerView + peekAmount);
      const slideAmount = slides || breakpointValues.slidesPerGroup;
      const newScrollLeft = scrollLeft + (slideWidth * slideAmount);

      containerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });

      const newSlide = calculateCurrentSlide();
      changeActiveSlide(event, newSlide);
    }
  };

  const prevSlide = (event, slides) => {
    if (containerRef.current) {
      const { offsetWidth, scrollLeft } = containerRef.current;
      const slideWidth = getSlideWidth(offsetWidth, breakpointValues.slidesPerView + peekAmount);
      const slideAmount = slides || breakpointValues.slidesPerGroup;
      const newScrollLeft = scrollLeft - (slideWidth * slideAmount);

      containerRef.current.scrollTo({
        left: Math.max(0, newScrollLeft),
        behavior: 'smooth'
      });

      const newSlide = calculateCurrentSlide();
      changeActiveSlide(event, newSlide);
    }
  };

  const carouselClasses = classNames('sui-overflow-x-scroll sui-flex sui-h-auto sui-carousel-scrollbar sui-py-4', {
    'sui-mb-4': !disableMargin
  });

  const isAtBeginning = currentSlide === 0;
  const isAtEnd = currentSlide >= children.length - breakpointValues.slidesPerView;

  const leftButtonClasses = 'sui-absolute sui-top-[40%] sui-flex sui-items-center sui-left-4 sui-z-tooltip';
  const rightButtonClasses = 'sui-absolute sui-top-[40%] sui-flex sui-items-center sui-right-4 sui-z-tooltip';

  const leftContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete] supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100': !isAtBeginning
  }, leftButtonClasses);
  const shadowLeftContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', leftButtonClasses);
  const rightContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete] supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100': !isAtEnd
  }, rightButtonClasses);
  const shadowRightContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', rightButtonClasses);

  const header = (
    <>
      {title != null || action != null ? (
        <div className="sui-flex sui-justify-between">
          {title}
          {action ? <div className="sui-m-2">{action}</div> : null}
        </div>
      ) : null}
    </>
  );

  const handleKeyDown = (event) => {
    const nextItemKey = 'ArrowRight';
    const previousItemKey = 'ArrowLeft';
    if (event.key === 'Enter' && event.currentTarget !== document.activeElement) {
      return;
    }
    const containerElement = event.currentTarget;
    const cardAnchor = event.currentTarget?.querySelector('a[tabindex="0"],button');
    if (event.key === nextItemKey) {
      const focusedIndex = Array.from(containerElement.children).findIndex(
        (child) => child.contains(document.activeElement)
      );
      const nextIndex = focusedIndex >= 0 ? focusedIndex + 1 : currentSlide + 1;
      containerElement.children[nextIndex]?.querySelector('a[tabindex="0"],button')?.focus();
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === previousItemKey) {
      const focusedIndex = Array.from(containerElement.children).findIndex(
        (child) => child.contains(document.activeElement)
      );
      const prevIndex = focusedIndex >= 0 ? focusedIndex - 1 : currentSlide;
      containerElement.children[prevIndex]?.querySelector('a[tabindex="0"],button')?.focus();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const dragBind = useDrag(({
    event, down, movement: [mx], first, last, velocity
  }) => {
    if (event.pointerType === 'touch') return;
    if (first) {
      startScrollLeft.current = containerRef.current.scrollLeft;
    }
    if (down) {
      const currentScroll = startScrollLeft.current;
      const newScrollLeft = currentScroll - mx;
      containerRef.current.scrollLeft = Math.max(0, newScrollLeft);
    }
    if (last) {
      if (velocity[0] > 0.5) {
        if (mx < 0) {
          nextSlide(event, Math.min(Math.round(velocity[0] * 1.5), breakpointValues.slidesPerGroup));
        } else {
          prevSlide(event, Math.min(Math.round(velocity[0] * 1.5), breakpointValues.slidesPerGroup));
        }
      }
    }
  }, {
    filterTaps: true,
    pointer: { touch: false, keys: false }
  });

  const focusNextElement = () => {
    // Elements to consider within selection
    let focusableElements = 'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
    if (document.activeElement) {
      let elements = Array.prototype.filter.call(
        document.querySelectorAll(focusableElements),
        function (element) {
          // Visibility check for available elements
          return (element);
        }
      );
      const currentIndex = Array.prototype.indexOf.call(elements, skipListingRef.current);
      if (currentIndex > -1) {
        const nextElement = elements[currentIndex + children.length + 3] || elements[currentIndex];
        nextElement.focus();
      }
    }
  };

  const handleButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    focusNextElement();
  };

  return (
    <div className="sui-group/sui-carousel sui-relative sui-w-full">
      <div className="sui-absolute sui-z-800 sui-top-2 sui-left-2 [clip-path:circle(0%)] focus-within:[clip-path:none]">
        <Button ref={skipListingRef} onClick={handleButton}>Skip Listing</Button>
      </div>
      {header}
      <div
        ref={containerRef}
        className={carouselClasses}
        style={{ gap: `${spaceBetween}px` }}
        {...dragBind()}
        onKeyDown={handleKeyDown}
      >
        {children.map((slide, index) => (
          <div
            key={`slide-${index}`}
            className="sui-carousel-slide sui-block sui-flex-shrink-0 sui-touch-pan-x [&>*]:sui-h-full"
            style={{ width: 'auto' }}
            role="group"
            aria-label={`${index + 1} of ${children.length}`}
          >
            {slide}
          </div>
        ))}
      </div>
      {!hideControls && (
        <div className="sui-flex sui-justify-center sui-items-center">
          <div className={leftContainerClasses}>
            <Fab
              tabIndex={-1}
              disabled={isAtBeginning}
              onClick={prevSlide}
            >
              <ArrowBack size="small" />
            </Fab>
          </div>
          <div className={shadowLeftContainerClasses}>
            <Fab
              aria-label={isAtBeginning ? 'This is the first slide' : 'Previous slide'}
              disabled={isAtBeginning && !alwaysShowControls}
              onClick={prevSlide}
            >
              <ArrowBack size="small" />
            </Fab>
          </div>
          <div className={rightContainerClasses}>
            <Fab
              tabIndex={-1}
              disabled={isAtEnd}
              onClick={nextSlide}
            >
              <ArrowForward size="small" />
            </Fab>
          </div>
          <div className={shadowRightContainerClasses}>
            <Fab
              aria-label={isAtEnd ? 'This is the last slide' : 'Next slide'}
              onClick={nextSlide}
              disabled={isAtEnd && !alwaysShowControls}
            >
              <ArrowForward size="small" />
            </Fab>
          </div>
        </div>
      )}
    </div>
  );
};

Filmstrip.displayName = 'Filmstrip';

Filmstrip.propTypes = {
  /**
   * The action that goes in the upper right corner of carousel
   */
  action: PropTypes.node,
  /**
   * Determines active slide on initial render. Also updates active slide when changed.
   */
  activeIndex: PropTypes.number,
  /**
   * If `true` will always show the controls.
   * @ignore
  */
  alwaysShowControls: PropTypes.bool,
  /**
   * Number of slides to display based on breakpoint
   */
  breakpoints: PropTypes.shape({
    sm: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    md: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    lg: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    xl: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    '2xl': PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    })
  }),
  /**
   * The carousel slides
   */
  children: PropTypes.node,
  /**
   * Disable the default margin of 32px (mb-8) under the Carousel.
   * When set to true, the Carousel will have no margin below it.
   * @default false
  */
  disableMargin: PropTypes.bool,
  /**
   * If `true` will hide the carousel controls. (Note that hiding the controls will impact accesibility, especially when
   * the carousel has more than one slide. This prop won't prevent the scrollbar from showing up in these occasions.)
   * @ignore
  */
  hideControls: PropTypes.bool,
  /**
   * Logic to be called when the active slide changes
   */
  onChange: PropTypes.func,
  /**
   * Determines how much are the Carousel slides allowed to peek. Only works when the Carousel type is set to 'peek'
   */
  peek: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * Number of slides to transition when clicking next/prev
   * @default 1
   */
  slidesPerGroup: PropTypes.number,
  /**
   * Number of slides to display at once
   * @default 2
   */
  slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  /**
   * The space between slides
   * @default 16
   */
  spaceBetween: PropTypes.number,
  /**
   * @ignore
   * Title of carousel in upper left corner
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

export { Filmstrip };